import React, { useEffect, useRef } from 'react';
import { Droplet, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import type { User } from '../../../shared/contracts';

export function LockedAccountOverlay() {
  const { user, logout, updateUser } = useAuth();
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Poll for unlock status
  useEffect(() => {
    // If not locked, don't poll
    if (!user?.isLocked) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      return;
    }

    const pollUnlockStatus = async () => {
      try {
        const authToken = localStorage.getItem('auth_token');
        if (!authToken) return;

        const response = await api.get<{ user: User }>('/auth/me', {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const currentLocked = response.user?.isLocked ?? false;

        // Account is unlocked!
        if (!currentLocked && user?.isLocked) {
          console.log('[LockedAccountOverlay] Account unlocked, updating state');
          updateUser({ ...response.user, isLocked: false });
          localStorage.setItem('user', JSON.stringify({ ...response.user, isLocked: false }));
        }
      } catch (error: any) {
        // Silently ignore errors during polling
        console.log('[LockedAccountOverlay] Polling error:', error?.message?.substring(0, 50));
      }
    };

    // Start polling immediately
    pollUnlockStatus();
    pollIntervalRef.current = setInterval(pollUnlockStatus, 3000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [user?.isLocked, user, updateUser]);

  const handleLogout = async () => {
    console.log('[LockedAccountOverlay] User clicked logout');
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    await logout();
  };

  // If user is not locked, don't render anything
  if (!user?.isLocked) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-2xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <Droplet className="h-12 w-12 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white">AquaFlow</h1>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Account Locked</h2>
          </div>

          {/* Message Card */}
          <div className="bg-white bg-opacity-10 rounded-xl p-6 mb-8 border border-white border-opacity-20">
            <p className="text-white text-center mb-6 leading-relaxed">
              Your account has been locked by your company administrator.
            </p>

            <div className="bg-white bg-opacity-10 rounded-lg p-4 border-l-4 border-white">
              <h3 className="font-semibold text-white mb-2">Next Steps:</h3>
              <p className="text-white text-sm leading-relaxed">
                Please contact your company administrator to unlock your account and regain access.
              </p>
            </div>
          </div>

          {/* User Email */}
          {user?.email && (
            <div className="text-center mb-8">
              <p className="text-white text-opacity-70 text-sm mb-1">Account Email</p>
              <p className="text-white font-medium break-all">{user.email}</p>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full px-4 py-3 rounded-lg text-cyan-700 bg-white hover:bg-gray-100 transition-colors font-semibold flex items-center justify-center"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
